// src/components/NotificationBell.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBell,
  FaBellSlash,
  FaCalendarPlus,
  FaCheckCircle,
  FaTimesCircle,
  FaClipboardList,
  FaExchangeAlt,
  FaClock,
  FaMoneyBillAlt,
  FaChartLine,
  FaCommentDots,
} from 'react-icons/fa';
import useNotifications from '../hooks/useNotifications';
import styles from './NotificationBell.module.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Convert a Firestore Timestamp to a human-readable relative string. */
const relativeTime = (timestamp) => {
  if (!timestamp?.seconds) return '';
  const seconds = Math.floor(Date.now() / 1000) - timestamp.seconds;
  if (seconds < 60)  return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)  return `${minutes}m ago`;
  const hours   = Math.floor(minutes / 60);
  if (hours   < 24)  return `${hours}h ago`;
  const days    = Math.floor(hours   / 24);
  if (days    <  7)  return `${days}d ago`;
  return new Date(timestamp.seconds * 1000).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short',
  });
};

/** Icon + accent color mapped per notification type. */
const TYPE_META = {
  leave_request:        { icon: FaCalendarPlus,  color: '#3b82f6' },
  leave_approved:       { icon: FaCheckCircle,   color: '#16a34a' },
  leave_rejected:       { icon: FaTimesCircle,   color: '#dc2626' },
  task_assigned:        { icon: FaClipboardList, color: '#7c3aed' },
  task_updated:         { icon: FaExchangeAlt,   color: '#d97706' },
  attendance_checkin:   { icon: FaClock,         color: '#0891b2' },
  attendance_approved:  { icon: FaCheckCircle,   color: '#16a34a' },
  attendance_rejected:  { icon: FaTimesCircle,   color: '#dc2626' },
  salary_slip:          { icon: FaMoneyBillAlt,  color: '#059669' },
  performance_kpi:      { icon: FaChartLine,     color: '#7c3aed' },
  performance_metric:   { icon: FaChartLine,     color: '#7c3aed' },
  chat_message:         { icon: FaCommentDots,   color: '#0284c7' },
};
const getTypeMeta = (type) =>
  TYPE_META[type] || { icon: FaBell, color: '#64748b' };

// ─── Component ────────────────────────────────────────────────────────────────

const NotificationBell = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, markAllRead, markRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // ── Close panel on outside click ─────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ── Auto–mark all read the moment the panel opens ─────────────────────────
  useEffect(() => {
    if (open && unreadCount > 0) markAllRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = () => setOpen(v => !v);

  const handleItemClick = useCallback(
    async (notif) => {
      if (!notif.read) await markRead(notif.id);
      setOpen(false);
      navigate(notif.link || '/dashboard');
    },
    [markRead, navigate]
  );

  const recent = notifications.slice(0, 25);

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      {/* ── Bell button ── */}
      <button
        className={`${styles.bellBtn} ${open ? styles.bellBtnActive : ''}`}
        onClick={toggle}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        title="Notifications"
      >
        <FaBell className={styles.bellIcon} />
        {unreadCount > 0 && (
          <span className={styles.badge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {/* ── Dropdown panel ── */}
      {open && (
        <div className={styles.panel}>
          {/* Header */}
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>
              <FaBell style={{ marginRight: 6, opacity: 0.7 }} />
              Notifications
            </span>
            <div className={styles.headerActions}>
              {notifications.length > 0 && (
                <button className={styles.markAllBtn} onClick={markAllRead}>
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className={styles.list}>
            {recent.length === 0 ? (
              <div className={styles.empty}>
                <FaBellSlash className={styles.emptyIcon} />
                <p>All caught up!</p>
                <small>No notifications yet.</small>
              </div>
            ) : (
              recent.map(notif => {
                const { icon: IconComp, color } = getTypeMeta(notif.type);
                return (
                  <button
                    key={notif.id}
                    className={`${styles.item} ${!notif.read ? styles.unread : ''}`}
                    onClick={() => handleItemClick(notif)}
                  >
                    {/* Colored icon pill */}
                    <span className={styles.iconWrap} style={{ '--accent': color }}>
                      <IconComp />
                    </span>

                    {/* Text content */}
                    <span className={styles.content}>
                      <span className={styles.itemTitle}>{notif.title}</span>
                      <span className={styles.itemBody}>{notif.body}</span>
                      <span className={styles.itemTime}>{relativeTime(notif.createdAt)}</span>
                    </span>

                    {/* Unread indicator dot */}
                    {!notif.read && <span className={styles.dot} aria-hidden="true" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
